import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import Dialog from '@mui/material/Dialog';
import useLzFApiCall from '@lz/services/Api';
import { useLzFHideMediaBrowser } from '@lz/services/Utils';
import { useLzOnMount } from '@lz/services/Hooks';
import lzSAuthTokenAtom from '@lz-atoms/Auth/token';
import lzSAuthUserAtom from '@lz-atoms/Auth/user';
import lzSAuthLoadedAtom from '@lz-atoms/Auth/loaded';
import lzSCommonsMediaBrowserDataAtom from '@lz-atoms/Commons/mediaBrowserData';
import LzMMedia from '@lz/models/commons/Media';
import LzXLayoutBase from './LayoutBase/component';
import LzXDimmer from '../commons/Dimmer';
import LzXMediaBrowser from '../commons/Media/Browser';

export interface LzPLayoutPrivate {
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  bgImg?: string;
  noContainer?: boolean;
}

export default function LzXLayoutPrivate(props: LzPLayoutPrivate) {
  // Store state getters and setters
  const authTokenState = useAtomValue(lzSAuthTokenAtom);
  const authUserState = useAtomValue(lzSAuthUserAtom);
  const [authLoadedState, setSAuthLoadedState] = useAtom(lzSAuthLoadedAtom);
  const mediaBrowserDataState = useAtomValue(lzSCommonsMediaBrowserDataAtom);

  const navigate = useNavigate();
  const apiCall = useLzFApiCall();

  const lzFHideMediaBrowser = useLzFHideMediaBrowser();

  const checkLogin = async () => {
    // Check for logged in user
    if (!authTokenState || '' === authTokenState) {
      // TODO: Save current screen location to be redirected to after login/register
      setSAuthLoadedState(false);
      navigate('/login');
      return;
    }

    // Check if logged in but fresh data not loaded for this window
    if (!authLoadedState) {
      const data = await apiCall('load-auth-data');
      if (data) {
        setSAuthLoadedState(true);
      }
      // If failed to load auth data then go to login page
      else {
        navigate('/login');
      }
    }
  };

  useLzOnMount(() => {
    checkLogin();
  });

  const mediaBrowserFail = (msg: string) => {
    if (mediaBrowserDataState) {
      if (mediaBrowserDataState.failureCallback) {
        mediaBrowserDataState.failureCallback(msg);
      }
      lzFHideMediaBrowser();
    }
  };

  const mediaBrowserSuccess = (media: LzMMedia) => {
    if (mediaBrowserDataState) {
      if (mediaBrowserDataState.successCallback) {
        mediaBrowserDataState.successCallback(media);
      }
    }
  };

  // Render the component
  return (
    <LzXLayoutBase bgImg={props.bgImg}>
      <div className={'lz-layout-private ' + (props.className || '')}>
        {authUserState ? props.children : <LzXDimmer active />}
      </div>

      {mediaBrowserDataState && (
        <Dialog fullScreen open onClose={() => mediaBrowserFail('Cancelled by user!')}
          PaperProps={{
            style: {
              width: '90vw',
              height: '90vh',
              padding: 9,
              paddingBottom: 0,
              borderRadius: 3,
            },
          }}
        >
          <LzXMediaBrowser forSelection params={mediaBrowserDataState.params}
            onSelect={(media) => mediaBrowserSuccess(media as LzMMedia)} />
        </Dialog>
      )}
    </LzXLayoutBase>
  );
}
